import * as i0 from '@angular/core';
import { InjectionToken, booleanAttribute, Directive, Input, EventEmitter, Optional, Inject, SkipSelf, Output, NgModule } from '@angular/core';
import * as i1 from '@angular/cdk/collections';
import { Subject, Subscription } from 'rxjs';

/** Used to generate unique ID for each accordion. */
let nextId$1 = 0;
/**
 * Injection token that can be used to reference instances of `CdkAccordion`. It serves
 * as alternative token to the actual `CdkAccordion` class which could cause unnecessary
 * retention of the class and its directive metadata.
 */
const CDK_ACCORDION = /*#__PURE__*/new InjectionToken('CdkAccordion');
/**
 * Directive whose purpose is to manage the expanded state of CdkAccordionItem children.
 */
let CdkAccordion = /*#__PURE__*/(() => {
  class CdkAccordion {
    constructor() {
      /** Emits when the state of the accordion changes */
      this._stateChanges = new Subject();
      /** Stream that emits true/false when openAll/closeAll is triggered. */
      this._openCloseAllActions = new Subject();
      /** A readonly id value to use for unique selection coordination. */
      this.id = `cdk-accordion-${nextId$1++}`;
      /** Whether the accordion should allow multiple expanded accordion items simultaneously. */
      this.multi = false;
    }
    /** Opens all enabled accordion items in an accordion where multi is enabled. */
    openAll() {
      if (this.multi) {
        this._openCloseAllActions.next(true);
      }
    }
    /** Closes all enabled accordion items. */
    closeAll() {
      this._openCloseAllActions.next(false);
    }
    ngOnChanges(changes) {
      this._stateChanges.next(changes);
    }
    ngOnDestroy() {
      this._stateChanges.complete();
      this._openCloseAllActions.complete();
    }
    static {
      this.ɵfac = function CdkAccordion_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CdkAccordion)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: CdkAccordion,
        selectors: [["cdk-accordion"], ["", "cdkAccordion", ""]],
        inputs: {
          multi: [2, "multi", "multi", booleanAttribute]
        },
        exportAs: ["cdkAccordion"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: CDK_ACCORDION,
          useExisting: CdkAccordion
        }]), i0.ɵɵInputTransformsFeature, i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return CdkAccordion;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/** Used to generate unique ID for each accordion item. */
let nextId = 0;
/**
 * A basic directive expected to be extended and decorated as a component.  Sets up all
 * events and attributes needed to be managed by a CdkAccordion parent.
 */
let CdkAccordionItem = /*#__PURE__*/(() => {
  class CdkAccordionItem {
    /** Whether the AccordionItem is expanded. */
    get expanded() {
      return this._expanded;
    }
    set expanded(expanded) {
      // Only emit events and update the internal value if the value changes.
      if (this._expanded !== expanded) {
        this._expanded = expanded;
        this.expandedChange.emit(expanded);
        if (expanded) {
          this.opened.emit();
          /**
           * In the unique selection dispatcher, the id parameter is the id of the CdkAccordionItem,
           * the name value is the id of the accordion.
           */
          const accordionId = this.accordion ? this.accordion.id : this.id;
          this._expansionDispatcher.notify(this.id, accordionId);
        } else {
          this.closed.emit();
        }
        // Ensures that the animation will run when the value is set outside of an `@Input`.
        // This includes cases like the open, close and toggle methods.
        this._changeDetectorRef.markForCheck();
      }
    }
    constructor(accordion, _changeDetectorRef, _expansionDispatcher) {
      this.accordion = accordion;
      this._changeDetectorRef = _changeDetectorRef;
      this._expansionDispatcher = _expansionDispatcher;
      /** Subscription to openAll/closeAll events. */
      this._openCloseAllSubscription = Subscription.EMPTY;
      /** Event emitted every time the AccordionItem is closed. */
      this.closed = new EventEmitter();
      /** Event emitted every time the AccordionItem is opened. */
      this.opened = new EventEmitter();
      /** Event emitted when the AccordionItem is destroyed. */
      this.destroyed = new EventEmitter();
      /**
       * Emits whenever the expanded state of the accordion changes.
       * Primarily used to facilitate two-way binding.
       * @docs-private
       */
      this.expandedChange = new EventEmitter();
      /** The unique AccordionItem id. */
      this.id = `cdk-accordion-child-${nextId++}`;
      this._expanded = false;
      /** Whether the AccordionItem is disabled. */
      this.disabled = false;
      /** Unregister function for _expansionDispatcher. */
      this._removeUniqueSelectionListener = () => {};
      this._removeUniqueSelectionListener = _expansionDispatcher.listen((id, accordionId) => {
        if (this.accordion && !this.accordion.multi && this.accordion.id === accordionId && this.id !== id) {
          this.expanded = false;
        }
      });
      // When an accordion item is hosted in an accordion, subscribe to open/close events.
      if (this.accordion) {
        this._openCloseAllSubscription = this._subscribeToOpenCloseAllActions();
      }
    }
    /** Emits an event for the accordion item being destroyed. */
    ngOnDestroy() {
      this.opened.complete();
      this.closed.complete();
      this.destroyed.emit();
      this.destroyed.complete();
      this._removeUniqueSelectionListener();
      this._openCloseAllSubscription.unsubscribe();
    }
    /** Toggles the expanded state of the accordion item. */
    toggle() {
      if (!this.disabled) {
        this.expanded = !this.expanded;
      }
    }
    /** Sets the expanded state of the accordion item to false. */
    close() {
      if (!this.disabled) {
        this.expanded = false;
      }
    }
    /** Sets the expanded state of the accordion item to true. */
    open() {
      if (!this.disabled) {
        this.expanded = true;
      }
    }
    _subscribeToOpenCloseAllActions() {
      return this.accordion._openCloseAllActions.subscribe(expanded => {
        // Only change expanded state if item is enabled
        if (!this.disabled) {
          this.expanded = expanded;
        }
      });
    }
    static {
      this.ɵfac = function CdkAccordionItem_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CdkAccordionItem)(i0.ɵɵdirectiveInject(CDK_ACCORDION, 12), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.UniqueSelectionDispatcher));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: CdkAccordionItem,
        selectors: [["cdk-accordion-item"], ["", "cdkAccordionItem", ""]],
        inputs: {
          expanded: [2, "expanded", "expanded", booleanAttribute],
          disabled: [2, "disabled", "disabled", booleanAttribute]
        },
        outputs: {
          closed: "closed",
          opened: "opened",
          destroyed: "destroyed",
          expandedChange: "expandedChange"
        },
        exportAs: ["cdkAccordionItem"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([
        // Provide `CDK_ACCORDION` as undefined to prevent nested accordion items from
        // registering to the same accordion.
        {
          provide: CDK_ACCORDION,
          useValue: undefined
        }]), i0.ɵɵInputTransformsFeature]
      });
    }
  }
  return CdkAccordionItem;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CdkAccordionModule = /*#__PURE__*/(() => {
  class CdkAccordionModule {
    static {
      this.ɵfac = function CdkAccordionModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CdkAccordionModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: CdkAccordionModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return CdkAccordionModule;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { CDK_ACCORDION, CdkAccordion, CdkAccordionItem, CdkAccordionModule };
